import type { loginResponse } from "~/types/api/login"
import type { ClientList } from "~/types/api/user"
import type { AccessLevel } from "~/types/api/user/accesslevel"
import type { RoleName, UserRole, UserRoleResponse } from "~/types/api/user/role"
import type {
    CreateSystemUserEndpointResponse,
    CreateSystemUserRequestBody,
    GetClientsAndRolesResponse,
    GetSystemUsersAccessDetailsResponse,
    SaveSystemUserAccessDetailsResponse,
    SaveUserManagementAssignmentsRequestBody,
} from "~/utils/api/user/user-management-response"

export class UserAPI {

 /**
   * Returns the average of two numbers.
   *
   * @remarks
   * This method is part of the {@link core-library#Statistics | Statistics subsystem}.
   *
   * @param x - The first input number
   * @param y - The second input number
   * @returns The arithmetic mean of `x` and `y`
   *
   * @beta
   */
    async logUserIn(client: string, username: string, password: string, redirectUrl?: string, rememberMe: boolean = false): Promise<Response> {
        try {
        const response = await $fetch.raw<loginResponse>('/api/login', {
            method: 'POST',
            body: {
                client: client,
                username: username,
                password: password,
                redirectUrl: redirectUrl,
                rememberMe: rememberMe
            },
            credentials: 'include',
            headers:useRequestHeaders()
        })
        return response._data
    } catch(err) {
            throw createError({
                status: err.data.statusCode,
                message: err.data.statusMessage
            })
        }
    }

    async getClients(): Promise<ClientList[]> {
        const response = await $fetch<ClientList[]>(`/api/user/clientlist`, {
            headers:useRequestHeaders(),
            query: {
                userid: 123
            }
        }).catch(err => {
            throw createError({status: err.statusCode, message: err.message})
            switch(err.statusCode) {
                case 401:
                    return {status: err.statusCode, message: err.message}
                default:
                    return {status: 0, message: 'No idea man'}
            }
        })
        return response
    }

    async getAccessLevel(): Promise<AccessLevel> {
        try {
            const response = await $fetch<AccessLevel>(`/api/user/accesslevel`, {
                headers:useRequestHeaders()
            })
            return response
        } catch(err) {
            if(err.statusCode == 403) {
                console.log('userapi.ts: Redirecting to login - user has no accessLevel', err.statusCode)
                if (import.meta.client) {
                    window.location.assign(`/login?redirect_uri=${encodeURIComponent(window.location.pathname)}`)
                }
            } else {
                throw createError({status: err.statusCode, message: err.message})
            }
        }
    }

    async getUserManagementClientsAndRoles(): Promise<GetClientsAndRolesResponse> {
        return await $fetch<GetClientsAndRolesResponse>('/api/user/management/clients-and-roles', {
            headers: useRequestHeaders(),
        }).catch((err) => {
            throw createError({ status: err.statusCode, message: err.message })
        })
    }

    async getUserManagementSystemUsersAccess(): Promise<GetSystemUsersAccessDetailsResponse> {
        return await $fetch<GetSystemUsersAccessDetailsResponse>(
            '/api/user/management/system-users-access',
            {
                headers: useRequestHeaders(),
            }
        ).catch((err) => {
            throw createError({ status: err.statusCode, message: err.message })
        })
    }

    /**
     * Saves assignments: `{ updatedByUserId, targetUserId, clientRoleInfos, email?, password? }`.
     * Empty `email` / `password` means leave unchanged; non-empty triggers profile update upstream.
     */
    async saveUserManagementAssignments(
        body: SaveUserManagementAssignmentsRequestBody
    ): Promise<SaveSystemUserAccessDetailsResponse> {
        return await $fetch<SaveSystemUserAccessDetailsResponse>(
            '/api/user/management/save-assignments',
            {
                method: 'POST',
                headers: useRequestHeaders(),
                body,
            }
        ).catch((err) => {
            throw createError({ status: err.statusCode, message: err.message })
        })
    }

    /**
     * Creates a system user via `UserManagementAPI` / `createSystemUser` (`userDetails` on the wire), then saves access; UI sends `username` from the email local-part.
     */
    async createSystemUser(
        body: CreateSystemUserRequestBody
    ): Promise<CreateSystemUserEndpointResponse> {
        return await $fetch<CreateSystemUserEndpointResponse>('/api/user/management/create-system-user', {
            method: 'POST',
            headers: useRequestHeaders(),
            body,
        }).catch((err) => {
            throw createError({ status: err.statusCode, message: err.message })
        })
    }

    async checkAccess(accessLevel: RoleName[]): Promise<boolean> {
        const response = await $fetch<UserRoleResponse>(`/api/user/role`, {
            headers: useRequestHeaders(),
        }).catch((err) => {
            if (err.statusCode == 403) {
                if (import.meta.client) {
                    const path = window.location.pathname + window.location.search
                    window.location.assign(`/login?redirect_uri=${encodeURIComponent(path)}`)
                }
                return undefined
            }
            throw createError({ status: err.statusCode, message: err.message })
        })
        if (!response || !response?.userRoles) {
            return false
        }
        return response.userRoles.some((role: UserRole) => accessLevel.includes(role.role_name))
    }
}
